import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import {
  Bank,
  Button,
  CardList,
  Col,
  Drawer,
  Empty,
  Image,
  Radio,
  Row,
  Space,
  Typography,
  Upload,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { Add, Close } from "@payping/icons";
import { useNavigate } from "react-router-dom";
import { DisplayModeEnum, ListCardProps } from "@payping/web-components/components/List/types";
import { PaymentGateway, useIpgTypeMap } from "@/pages/Settings/Terminal/data";
import { getUserBusinessinfo, getUserTerminalList } from "@payping/redux/actions";
import { DashboardRoutes } from "@/router/constant";
import { UploadChangeParam, UploadFile } from "@payping/web-components/components/Upload/types";
import { FileTypeEnum, UserTerminalLogoStatusResponseModel, fileApis, uploadApis, userApis } from "@payping/api";
import { buildUrl } from "@payping/utilities-shared/url";
import { URLS } from "@payping/utilities-shared/consts";

const MobileTerminalList: React.FC = () => {
  const { settings } = DashboardRoutes;
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const terminalList = useAppSelector((state) => state.reports.terminalList);
  const ipgTypeMap = useIpgTypeMap();
  const dispatch = useAppDispatch();
  const isDefaultPlan = useAppSelector((state) => state.user.panelInfo?.currentPlan?.isDefaultPlan);
  const [selectedImage, setSelectedImage] = useState<PaymentGateway>(PaymentGateway.Sadad);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [{ terminalLogoVerification, terminalLogo }, setTerminalStatus] = useState<UserTerminalLogoStatusResponseModel>(
    {
      terminalLogo: "",
      terminalLogoVerification: true,
    },
  );

  useEffect(() => {
    if (terminalLogo) {
      setFileList([
        {
          name: "image",
          size: 0,
          type: "image/*",
          uid: "0",
          url: terminalLogo,
        },
      ]);
    }
  }, [terminalLogo]);

  const ImagePaymentGateway: Record<PaymentGateway, string> = {
    [PaymentGateway.Sadad]: buildUrl(URLS.STORAGE_URL, "img/document/terminals/Sadad_Desktop.png").toString(),
    [PaymentGateway.Mellat]: buildUrl(URLS.STORAGE_URL, "img/document/terminals/Behpardakh_Desktop.png").toString(),
    [PaymentGateway.Pep]: buildUrl(URLS.STORAGE_URL, "img/document/terminals/Pasargad_Desktop.png").toString(),
    [PaymentGateway.Saman]: buildUrl(URLS.STORAGE_URL, "img/document/terminals/Sep_Desktop.png").toString(),
    [PaymentGateway.Mabna]: buildUrl(URLS.STORAGE_URL, "img/document/terminals/Sepehr_Desktop.png").toString(),
  };

  const { loading: loadingTerminalList } = useMessagedAsync(() => dispatch(getUserTerminalList()), [], {
    success: false,
    useProgress: true,
  });

  const { loading } = useMessagedAsync(
    async () => {
      await dispatch(getUserBusinessinfo());
      const status = await userApis.terminalLogoStatusWithCancellationTokenGet();
      setTerminalStatus(status);
    },
    [],
    { useProgress: true },
  );

  const [{ loading: loadingUpload }, handleUpload] = useMessagedAsyncFn(
    // TODO: import type from rc-upload in frontcomponents
    async (options: any) => {
      const { file, onSuccess, onError } = options;
      uploadApis
        .miscWithFilePost(file)
        .then(async (fileName) => {
          await fileApis.addFileWithRequestModelWithCancellationTokenPost({
            fileName,
            type: FileTypeEnum.TerminalLogo,
          });
          onSuccess(fileName, file);
        })
        .catch(onError);
      return {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        abort() {},
      };
    },
    [],
  );

  const data: ListCardProps["dataSource"] = terminalList.map((terminal) => ({
    key: Number(terminal.terminalCode),
    image: { type: "avatar", content: <Bank.Icon showTooltip ipg={terminal.ipgType} /> },
    title: {
      primary: ipgTypeMap(terminal.ipgType),
      tag: terminal.isBusiness ? t("common.legal") : t("common.natural"),
    },
    /*
    // TODO NEED Corrections
    descriptions: [
      { value: terminal.acceptorCode, label: t("terminals.table.acceptorCode") },
      { value: terminal.terminalId, label: t("terminals.table.terminalId") },
    ],
    */
    descriptions: (
      <Space direction={"vertical"} className={"tw-w-full"}>
        <Typography.Text>
          {terminal.acceptorCode} {t("terminals.table.acceptorCode")}
        </Typography.Text>
        <Typography.Text>
          {terminal.terminalCode} {t("terminals.table.terminalId")}
        </Typography.Text>
      </Space>
    ),
  }));
  return (
    <Drawer
      open={true}
      placement={"bottom"}
      height={"100%"}
      title={t("terminals.myTerminals")}
      closeIcon={false}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            navigate(settings.mRoot.url);
          }}
        />
      }
    >
      <Row gutter={[0, 16]}>
        <Col xs={24}>
          <CardList
            loading={!data.length ? loadingTerminalList : false}
            dataSource={data}
            grid={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1, column: 1 }}
            mode={DisplayModeEnum.List}
            className={"background--gray100"}
            locale={{
              emptyText: (
                <Empty
                  description={
                    <Typography.Text className={"color--black"}>{t("terminals.emptyTerminal")}</Typography.Text>
                  }
                />
              ),
            }}
          />
        </Col>
        <Col xs={24}>
          <Typography.Title level={5}>{t("settings.terminal.dedicatedLogo")}</Typography.Title>
          <Typography.Text type={"secondary"}>
            {isDefaultPlan
              ? t("settings.terminal.dedicatedLogoContentDefaultPlan")
              : t("settings.terminal.dedicatedLogoContent")}
          </Typography.Text>
        </Col>
        <Col xs={24}>
          {isDefaultPlan ? (
            <Button type={"primary"} onClick={() => navigate(DashboardRoutes.user.upgrade.url)}>
              {t("mySales.withdrawTab.upgradePanel")}
            </Button>
          ) : (
            <Upload
              disabled={loading || terminalLogoVerification || loadingUpload}
              listType={"picture-card"}
              customRequest={(options) => handleUpload(options)}
              onChange={(info: UploadChangeParam): void => setFileList(info.fileList)}
              fileList={fileList}
              showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
              allowedExtensions={["jpg", "jpeg", "png"]}
            >
              {!fileList.length && (
                <div>
                  <Add width={"20px"} />
                  <div className={"ant-upload-text"}>{t("settings.terminal.image")}</div>
                </div>
              )}
            </Upload>
          )}
        </Col>
      </Row>
      <Space className={"tw-mt-8 tw-w-full"} direction={"vertical"} size={"large"}>
        <Typography.Text strong>{t("settings.terminal.previewOnTheTerminal")}</Typography.Text>
        <Radio.Group onChange={(e) => setSelectedImage(e.target.value)} value={selectedImage}>
          <Radio.Button value={PaymentGateway.Sadad}>{t("common.gateway.Sadad")}</Radio.Button>
          <Radio.Button value={PaymentGateway.Mellat}>{t("common.gateway.Mellat")}</Radio.Button>
          <Radio.Button value={PaymentGateway.Pep}>{t("common.gateway.Pep")}</Radio.Button>
          <Radio.Button value={PaymentGateway.Saman}>{t("common.gateway.SepOnlinePG")}</Radio.Button>
          <Radio.Button value={PaymentGateway.Mabna}>{t("common.gateway.Mabna")}</Radio.Button>
        </Radio.Group>
        <Image preview={false} width={"100%"} src={ImagePaymentGateway[selectedImage]} />
      </Space>
    </Drawer>
  );
};

export default MobileTerminalList;
